import { Close, FilterList } from '@mui/icons-material';
import { Box, Button, Grid, MenuItem } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
import { SMBSelect } from '../../../../components';

interface IPunchlistFilter {
  id: number;
  name: string;
}
interface IPunchlistDetailsFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: {
    clearSelectedVendorId?: boolean;
    clearSelectedEmployeeId?: boolean;
  }) => void;
  hasAppliedFilters?: boolean;
  setHasAppliedFilters: (val: boolean) => void;
  vendors: IPunchlistFilter[] | null;
  selectedVendorId: string | null;
  setSelectedVendorId: (val: string | null) => void;
  employees: IPunchlistFilter[] | null;
  selectedEmployeeId: string | null;
  setSelectedEmployeeId: (val: string | null) => void;
}

export const PunchlistDetailsFilters: FC<IPunchlistDetailsFilters> = ({
  isLoading,
  applyFilters,
  hasAppliedFilters,
  setHasAppliedFilters,
  vendors,
  selectedVendorId,
  setSelectedVendorId,
  employees,
  selectedEmployeeId,
  setSelectedEmployeeId
}) => {
  const classes = useStyles();

  const handleDisableButton = () => {
    if (isLoading) return true;
    return false;
  };

  const hasEmployees = !isLoading && employees?.length > 0;

  return (
    <>
      <Box>
        <Grid container alignItems="center" spacing={1} marginBottom={theme => theme.spacing(0.5)}>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              variant="outlined"
              label={isLoading ? 'Loading...' : 'Vendor'}
              value={selectedVendorId ?? ''}
              disabled={isLoading}
              onChange={e => {
                setSelectedVendorId(e.target.value as string);
              }}
              handleClear={() => {
                setSelectedVendorId('none');
                setHasAppliedFilters(selectedEmployeeId === 'none' ? false : true);
                applyFilters({ clearSelectedVendorId: true, clearSelectedEmployeeId: false });
              }}
            >
              <MenuItem key={`noAssignedVendor`} value={'none'}>
                No Vendor Assigned
              </MenuItem>
              {vendors?.map((vendor, index) => {
                return (
                  <MenuItem key={`${vendor.id}`} value={vendor.id}>
                    {vendor.name}
                  </MenuItem>
                );
              })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              variant="outlined"
              label={isLoading ? 'Loading...' : 'Internal Employee'}
              value={selectedEmployeeId ?? ''}
              disabled={isLoading}
              onChange={e => {
                setSelectedEmployeeId(e.target.value as string);
              }}
              handleClear={() => {
                setSelectedEmployeeId('none');
                setHasAppliedFilters(selectedVendorId === 'none' ? false : true);
                applyFilters({ clearSelectedVendorId: false, clearSelectedEmployeeId: true });
              }}
            >
              <MenuItem key={`noAssignedEmployee`} value={'none'}>
                {hasEmployees ? 'No Employee Assigned' : 'No Options Available'}
              </MenuItem>
              {employees?.map((employee, index) => {
                return (
                  <MenuItem key={`${employee.id}`} value={employee.id}>
                    {employee.name}
                  </MenuItem>
                );
              })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={4} lg={4}>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              disabled={handleDisableButton()}
              startIcon={<FilterList />}
              onClick={() => {
                if (selectedVendorId === 'none' && selectedEmployeeId === 'none') {
                  setHasAppliedFilters(false);
                  // if more filters are added, we'll have to change this to applyFilters() to prevent other filters from being removed
                  applyFilters();
                } else {
                  setHasAppliedFilters(true);
                  applyFilters();
                }
              }}
            >
              Apply Filters
            </Button>
            {hasAppliedFilters && (
              <Button
                color="inherit"
                className={clsx(classes.button, classes.resetButton)}
                variant="contained"
                disabled={isLoading}
                startIcon={<Close />}
                onClick={() => {
                  setHasAppliedFilters(false);
                  applyFilters({ clearSelectedVendorId: true, clearSelectedEmployeeId: true });
                }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0.5)
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  }
}));
